import React, { useState, useEffect, useRef, createRef, memo } from "react";
import {
  Constants,
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
// import { BottomBar } from "./components/BottomBar";
import { SidebarConatiner } from "../sidebar/SidebarContainer";
import MemorizedParticipantView from "../../features/meeting/components/ParticipantView";
import { PresenterView } from "../../features/meeting/PresenterView";
import { nameTructed, trimSnackBarText } from "../../../utils/helper";
import WaitingToJoinScreen from "../../features/screens/WaitingToJoinScreen";
import ConfirmBox from "../../../components/commonComponents/ConfirmBox";
import CountdownOverlay from "./CountdownOverlay";
import useIsMobile from "../../../hooks/useIsMobile";
import useIsTab from "../../../hooks/useIsTab";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useMeetingAppContext } from "../../../hooks/MeetingAppContextDef";
import AudioBot from "./components/AudioBot";
import UserVoiceRecorder from "./components/UserVoiceRecorder";
import {
  connectInterviewSocket,
  closeAllSockets,
  setChatMessagePublisher,
  setBotMessagePublisher,
  allowBotToStart,
  closeInterviewSocketWithCallback,
  sendPeripheralRecord,
  lockInterviewSocket,
} from "../../../hooks/socketManager";
import EndInterviewOverlay from "./components/EndInterviewOverlay";
import { useAutoChunkedRecording } from "../../../hooks/useAutoChunkedRecording";
import useBotSpeech, { getBotQueueStatus } from "../../../hooks/useBotSpeech";
import whiteLogoImg from "../../../assets/images/needle-in-the-haystack-white-logo.svg";
import { apiService } from "../../../services/apiService";
import { WarningModal } from "../../../components/commonComponents/WarningModal";
import { useMultiMonitorDetection } from "../../../hooks/useMultiMonitorDetection";
import MonitorDetectionOverlay from "./components/MonitorDetectionOverlay";
import useHeadphoneMonitor from "../../../hooks/useHeadphoneMonitor";
import {
  startMoonshotSocket,
  closeMoonshotSocket,
} from "../../../hooks/socketManager";
import MoonshotProctoringOverlay from "./components/MoonshotProctoringOverlay";
import {
  updateSession,
  listenSession,
} from "../../../services/firestoreService";
import { listenDesktopAgent } from "../../../services/desktopAgentService";
import { WhiteboardView } from "./WhiteboardView";
// import WhiteboardParticipantCard from "./WhiteboardParticipantCard";
import WhiteboardPanelSidebar from "./WhiteboardPanelSidebar";
import {
  getCodingQuestions,
  getWhiteboardCodingQuestions,
} from "../../../services/apiInterviewService";
import CodingOverlay from "./CodingOverlay";

export function MeetingContainer({
  meetingId,
  onMeetingLeave,
  setIsMeetingLeft,
  participantName,
}) {
  const overlayStartedRef = useRef(false);
  const fbkReceivedRef = useRef(false);

  // Mirrors `showWhiteboard` state but readable synchronously from inside
  // event listeners that were subscribed once on mount (e.g. the FBK
  // handler below) — reading the state variable directly there would
  // always see its value from the first render.
  const showWhiteboardRef = useRef(false);
  const showCodingRef = useRef(false);
  const recordEventRef = useRef(null);
  const botAudioActiveRef = useRef(false);
  const moonshotPendingRef = useRef(false);
  const apiAbortControllerRef = useRef(null);
  const moonshotIntervalRef = useRef(null);
  const moonshotAbortRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const timeoutRef = useRef(null);

  const companyId = sessionStorage.getItem("companyId");
  const [interviewAlreadyRunning, setInterviewAlreadyRunning] = useState(false);
  const [interviewAlreadyCompleted, setInterviewAlreadyCompleted] = useState(
    () => {
      return sessionStorage.getItem("interviewAlreadyCompleted") === "true";
    },
  );
  const [showLeaveEndOverlay, setShowLeaveEndOverlay] = useState(false);
  const [showPendingRedirectOverlay, setShowPendingRedirectOverlay] =
    useState(false);
  const [pendingRedirectMessage, setPendingRedirectMessage] = useState("");
  const isBot = participantName?.toLowerCase() === "anushka";
  const { isBotSpeaking, queueStatus, isBotBusy } = useBotSpeech();
  const [activePanelSpeaker, setActivePanelSpeaker] = useState(null);

  const [queuedBotMessages, setQueuedBotMessages] = useState([]);

  const interviewId = sessionStorage.getItem("interviewId");
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showCountdown, setShowCountdown] = useState(true);
  const [readyToJoin, setReadyToJoin] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [useTranscriptionMode, setUseTranscriptionMode] = useState(false);
  const [showEndInterview, setShowEndInterview] = useState(false);
  const [endInterviewTriggered, setEndInterviewTriggered] = useState(false);
  const [isQuestionnaireActive, setIsQuestionnaireActive] = useState(false);

  // Add state for controlling overlay timing
  const [overlayDelayTimer, setOverlayDelayTimer] = useState(null);
  const [overlayDisplayTimer, setOverlayDisplayTimer] = useState(null);

  const [participantsData, setParticipantsData] = useState([]);
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] =
    useState(null);
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [showMoonshotWarning, setShowMoonshotWarning] = useState(false);

  const [showMoonshotOverlay, setShowMoonshotOverlay] = useState(false);
  const [desktopAgentRunning, setDesktopAgentRunning] = useState(true);
  const [showDesktopWarning, setShowDesktopWarning] = useState(false);
  const desktopPrevStateRef = useRef(true);
  const desktopWarningTimerRef = useRef(null);
  const batteryLowRef = useRef(false);
  // Low-battery audio refs
  const lowBatteryAudioPlayedRef = useRef(
    sessionStorage.getItem("lowBatteryAudioPlayed") === "true",
  );
  const lowBatteryPendingRef = useRef(false); // condition met, waiting for right moment
  const countdownDoneRef = useRef(false); // true after countdown finishes
  const batteryUnsubscribeRef = useRef(null);

  const isMobileProctoringEnabled =
    sessionStorage.getItem("mobileProctoringScreenshot") === "true";

  // ── Helper: check if bot is speaking or has messages queued ──
  const isBotCurrentlySpeaking = () => {
    try {
      const status = getBotQueueStatus();
      if (
        status?.isBusy ||
        status?.isPlaying ||
        (status?.queueLength ?? 0) > 0
      ) {
        return true;
      }
    } catch (e) {}

    if (botAudioActiveRef.current) return true;

    const botAudioEl = document.getElementById("botAudio");
    if (botAudioEl && !botAudioEl.paused && !botAudioEl.ended) {
      return true;
    }

    return false;
  };

  // ── Low-battery audio: plays /sounds/low-battery.mp3 exactly once ──
  const playLowBatteryAudio = () => {
    if (!isMobileProctoringEnabled) {
      lowBatteryPendingRef.current = false;
      return;
    }
    if (lowBatteryAudioPlayedRef.current) return;
    lowBatteryAudioPlayedRef.current = true;
    lowBatteryPendingRef.current = false;

    // Persist flag so on any rejoin/remount, listening and audio never trigger again
    sessionStorage.setItem("lowBatteryAudioPlayed", "true");

    // Stop battery listener immediately
    batteryUnsubscribeRef.current?.();

    window.__isLowBatteryAudioPlaying = true;

    window.__isLowBatteryAudioPlaying = true;
    window.dispatchEvent(new CustomEvent("low-battery-audio-start")); // NEW

    const audio = new Audio("/sounds/low-battery.mp3");
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.__isLowBatteryAudioPlaying = false;
      window.dispatchEvent(new CustomEvent("low-battery-audio-end")); // NEW
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("resume-bot-queue"));
      }, 50);
    };
    audio.onended = finish;
    audio.onerror = finish;
    audio.play().catch(finish);
    // Safety timeout in case ended never fires
    setTimeout(finish, 15000);
  };

  useEffect(() => {
    if (
      !isMobileProctoringEnabled ||
      !companyId ||
      !interviewId ||
      fbkReceivedRef.current ||
      sessionStorage.getItem("fbk-received") === "true" ||
      lowBatteryAudioPlayedRef.current ||
      sessionStorage.getItem("lowBatteryAudioPlayed") === "true"
    ) {
      return;
    }

    const documentId = `${companyId}_${interviewId}`;
    let unsubscribe = null;

    const startListening = () => {
      unsubscribe = listenSession(documentId, (data) => {
        if (
          lowBatteryAudioPlayedRef.current ||
          sessionStorage.getItem("lowBatteryAudioPlayed") === "true"
        ) {
          unsubscribe?.();
          return;
        }

        const isCharging =
          data?.is_charging === true || data?.is_charging === "true";

        if (
          fbkReceivedRef.current ||
          sessionStorage.getItem("fbk-received") === "true" ||
          isCharging
        ) {
          batteryLowRef.current = false;

          if (
            fbkReceivedRef.current ||
            sessionStorage.getItem("fbk-received") === "true"
          ) {
            unsubscribe?.();
          }

          return;
        }

        if (
          data?.battery_percentage === null ||
          data?.battery_percentage === ""
        ) {
          return;
        }

        const batteryPercentage = Number(data?.battery_percentage);
        if (!Number.isFinite(batteryPercentage)) return;

        if (batteryPercentage <= 10) {
          if (!batteryLowRef.current) {
            batteryLowRef.current = true;

            // Low-battery audio: trigger once
            if (!lowBatteryAudioPlayedRef.current) {
              if (!countdownDoneRef.current || isBotCurrentlySpeaking()) {
                // Bot is speaking or countdown active — defer until bot finishes
                lowBatteryPendingRef.current = true;
              } else {
                playLowBatteryAudio();
              }
            }
          }
          return;
        }

        batteryLowRef.current = false;
      });
      batteryUnsubscribeRef.current = unsubscribe;
    };

    // Start listening as soon as the countdown has finished.
    startListening();

    return () => {
      unsubscribe?.();
      batteryUnsubscribeRef.current = null;
      batteryLowRef.current = false;
    };
  }, [companyId, interviewId, isMobileProctoringEnabled]);

  const openProctoringOverlay = () => {
    if (!isMobileProctoringEnabled || interviewAlreadyCompleted) return;
    setShowMoonshotOverlay(true);
  };

  useEffect(() => {
    const handleFBKReceived = () => {
      fbkReceivedRef.current = true;
      batteryLowRef.current = false;

      if (moonshotIntervalRef.current) {
        clearInterval(moonshotIntervalRef.current);
        moonshotIntervalRef.current = null;
      }
      sessionStorage.removeItem("moonshotElapsedSeconds");

      if (isMobileProctoringEnabled) {
        markInterviewEndedOnFBK();
      }
      console.log("FBK received");
      setInterviewAlreadyCompleted(true);

      const check = readPreReconnectionCheck();
      const isCodingCompleted = check?.is_coding_completed === true;
      const isWhiteboardCompleted = check?.is_whiteboarding_completed === true;

      const isWhiteboard =
        sessionStorage.getItem("whiteboarding_env") === "true" &&
        !isWhiteboardCompleted;
      const isCoding =
        sessionStorage.getItem("coding_env") === "true" && !isCodingCompleted;

      if (isCoding) {
        if (!showCodingRef.current) {
          if (isWhiteboard)
            setPendingRedirectMessage(
              "You have completed the main interview. The Coding and Whiteboard sessions are still pending. Please complete them to finish your interview. Redirecting you now…",
            );
          else
            setPendingRedirectMessage(
              "You have completed the main interview. The Coding session is still pending. Please complete them to finish your interview. Redirecting you now…",
            );
          setShowPendingRedirectOverlay(true);
          setTimeout(() => {
            setShowPendingRedirectOverlay(false);
            openCodingOverlay();
          }, 5000);
        }
      } else if (isWhiteboard) {
        if (whiteboardIntervalRef.current) {
          clearInterval(whiteboardIntervalRef.current);
          whiteboardIntervalRef.current = null;
        }

        if (!showWhiteboardRef.current) {
          setPendingRedirectMessage(
            "You have completed the main interview. The Whiteboard session is still pending. Please complete them to finish your interview. Redirecting you now…",
          );
          setShowPendingRedirectOverlay(true);
          setTimeout(() => {
            setShowPendingRedirectOverlay(false);
            openWhiteboard();
          }, 5000);
        }
      } else {
        console.log("FBK received → stopping proctoring permanently");

        fbkReceivedRef.current = true;

        window.__interviewFrozen = false;
        closeProctoringOverlay();
        //  Cleanup everything
        cleanupListener();

        window.__interviewFrozen = false;
        closeProctoringOverlay();

        // Mark the interview complete right away — don't wait for
        // EndInterviewOverlay to mount later.
      }
    };

    window.addEventListener("fbk-received", handleFBKReceived);

    return () => {
      window.removeEventListener("fbk-received", handleFBKReceived);
    };
  }, []);

  useEffect(() => {
    const handleInterviewRunning = () => {
      console.warn("Switching to Interview Already Running screen");

      // Stop everything immediately
      window.dispatchEvent(new CustomEvent("disable-interview-guard"));
      window.dispatchEvent(new CustomEvent("force-stop-recording"));
      window.dispatchEvent(new CustomEvent("force-stop-all-media"));

      closeAllSockets();

      setInterviewAlreadyRunning(true);
    };

    window.addEventListener(
      "interview-already-running",
      handleInterviewRunning,
    );

    return () => {
      window.removeEventListener(
        "interview-already-running",
        handleInterviewRunning,
      );
    };
  }, []);

  useEffect(() => {
    const handleInterviewAlreadyCompleted = () => {
      console.warn(
        "CMPLTD received — suppressing countdown overlay, interview already completed",
      );

      setShowCountdown(false);
      setInterviewAlreadyCompleted(true);
    };

    window.addEventListener(
      "interview-already-completed",
      handleInterviewAlreadyCompleted,
    );

    return () => {
      window.removeEventListener(
        "interview-already-completed",
        handleInterviewAlreadyCompleted,
      );
    };
  }, []);

  const closeProctoringOverlay = () => {
    setShowMoonshotOverlay(false);
  };

  // Moved here from EndInterviewOverlay: this used to run only once that
  // overlay mounted, which could be well after FBK actually arrived. Now
  // it fires immediately from the fbk-received handler below, as soon as
  // we know the interview is really ending (not the whiteboard/coding
  // branches).
  const markInterviewEndedOnFBK = async () => {
    try {
      const companyId = sessionStorage.getItem("companyId");
      const interviewId = sessionStorage.getItem("interviewId");
      const documentId = `${companyId}_${interviewId}`;

      await updateSession(documentId, { status: 999 });

      console.log("Status updated to 999 (End Interview)");
    } catch (err) {
      console.error("Failed to update status 999", err);
    }
  };
  const isHeadphoneRequired =
    sessionStorage.getItem("headphoneRequirement") === "true";

  // Whiteboard state
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [whiteboardSessionId, setWhiteboardSessionId] = useState(() =>
    crypto.randomUUID(),
  );
  const [whiteboardQuestion, setWhiteboardQuestion] = useState(null);

  const [whiteboardQuestionIndex, setWhiteboardQuestionIndex] = useState(0);
  const [codingQuestion, setCodingQuestion] = useState(null);
  const [codingQuestionIndex, setCodingQuestionIndex] = useState(0);
  const readPreReconnectionCheck = () => {
    try {
      const raw = sessionStorage.getItem("preReconnectionCheck");
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.error(
        "[Whiteboard] Failed to parse preReconnectionCheck from sessionStorage:",
        err,
      );
      return null;
    }
  };

  const [whiteboardIsReconnect] = useState(() => {
    const check = readPreReconnectionCheck();
    return !!(
      check?.is_interview_completed === true &&
      check?.whiteboard_question_id &&
      !check?.is_whiteboarding_completed
    );
  });
  // const [whiteboardIsReconnect] = useState(false);
  const [isWhiteboardReconnect] = useState(
    () => sessionStorage.getItem("whiteboardReconnectPending") === "true",
  );
  const [whiteboardReconnectQuestionId] = useState(
    () => readPreReconnectionCheck()?.whiteboard_question_id || null,
  );
  const [whiteboardReconnectDuration] = useState(() => {
    const raw = parseFloat(readPreReconnectionCheck()?.whiteboarding_duration);
    return Number.isFinite(raw) ? raw : 0;
  });
  // Index of the LAST video chunk that was successfully streamed before the

  // falls back to starting at 0.
  const [whiteboardReconnectVideoIndex] = useState(() => {
    const raw = parseInt(
      readPreReconnectionCheck()?.whiteboarding_video_index,
      10,
    );
    return Number.isFinite(raw) ? raw : null;
  });
  const whiteboardReconnectHandledRef = useRef(false);
  // Mirrors whether the *currently open* whiteboard session is a resumed
  // one — passed straight through to WhiteboardView's isReconnect prop.
  const [activeWhiteboardIsReconnect, setActiveWhiteboardIsReconnect] =
    useState(false);

  const whiteboardExportRef = useRef(null); // populated by WhiteboardView
  const whiteboardClearRef = useRef(null); // populated by WhiteboardView
  const [showCoding, setShowCoding] = useState(false);

  const [codingSessionId, setCodingSessionId] = useState(() =>
    crypto.randomUUID(),
  );
  const [codingIsReconnect] = useState(() => {
    const check = readPreReconnectionCheck();
    return !!(
      check?.coding_question_id &&
      !check?.is_coding_completed &&
      check.is_interview_completed
    );
  });
  //const [codingIsReconnect] = useState(false);
  const [isCodingReconnect] = useState(
    () => sessionStorage.getItem("codingReconnectPending") === "true",
  );
  const [codingReconnectQuestionId] = useState(
    () => readPreReconnectionCheck()?.coding_question_id || null,
  );
  const [codingReconnectDuration] = useState(() => {
    const raw = parseFloat(readPreReconnectionCheck()?.coding_duration);
    return Number.isFinite(raw) ? raw : 0;
  });
  const [resetTimer, setResetTimer] = useState(false);
  const [codingReconnectVideoIndex, setCodingReconnectVideoIndex] = useState(
    () => {
      const raw = parseInt(readPreReconnectionCheck()?.coding_video_index, 10);
      return Number.isFinite(raw) ? raw : null;
    },
  );
  const codingReconnectHandledRef = useRef(false);
  const [activeCodingIsReconnect, setActiveCodingIsReconnect] = useState(false);
  useEffect(() => {
    sessionStorage.setItem(
      "isWHiteBoradOpen",
      showWhiteboard ? "true" : "false",
    );
    sessionStorage.setItem("isCodingOpen", showCoding ? "true" : "false");

    window.__WHITEBOARD_ACTIVE__ = showWhiteboard;
    window.__CODING_ACTIVE__ = showCoding;
    showWhiteboardRef.current = showWhiteboard;
    showCodingRef.current = showCoding;
  }, [showWhiteboard, showCoding]);

  // Headphone warning is a timed toast-style modal (same pattern as
  // triggerDesktopWarning below), not a live reflection of connection
  // state: it opens on a disconnect event, auto-closes after 10s, and can
  // also be dismissed early via OK. Detection itself is unchanged — only
  // how the warning is shown/dismissed.
  const [showHeadphoneWarning, setShowHeadphoneWarning] = useState(false);
  const headphoneWarningTimerRef = useRef(null);

  const triggerHeadphoneWarning = () => {
    setShowHeadphoneWarning(true);

    if (headphoneWarningTimerRef.current) {
      clearTimeout(headphoneWarningTimerRef.current);
    }

    // Auto-close after 10 sec
    headphoneWarningTimerRef.current = setTimeout(() => {
      setShowHeadphoneWarning(false);
    }, 10000);
  };

  const dismissHeadphoneWarning = () => {
    if (headphoneWarningTimerRef.current) {
      clearTimeout(headphoneWarningTimerRef.current);
      headphoneWarningTimerRef.current = null;
    }
    setShowHeadphoneWarning(false);
  };

  const {
    isHeadphoneConnected,
    needsManualConfirmation: headphoneNeedsManualConfirmation,
    confirmManually: confirmHeadphoneManually,
  } = useHeadphoneMonitor({
    enabled: isHeadphoneRequired,
    onConnect: () => {
      recordEventRef.current?.("headphone-connected");
      dismissHeadphoneWarning();
    },
    onDisconnect: () => {
      recordEventRef.current?.("headphone-disconnected");
      window.dispatchEvent(new CustomEvent("headphone-disconnected"));
      triggerHeadphoneWarning();
    },
  });

  const {
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    sideBarMode,
    setSideBarMode,
    queueBotMessage, // Add this
    processPendingBotMessage, // Add this
    pendingBotMessages, // Add this
  } = useMeetingAppContext();

  const mMeetingRef = useRef();
  const containerRef = createRef();
  const containerHeightRef = useRef();
  const containerWidthRef = useRef();

  // Move the callback functions before useMeeting hook
  const _handleMeetingLeft = () => {
    setIsMeetingLeft(true);
  };

  const _handleOnRecordingStateChanged = ({ status }) => {
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      toast(
        `${
          status === Constants.recordingEvents.RECORDING_STARTED
            ? "Meeting recording is started"
            : "Meeting recording is stopped."
        }`,
        {
          position: "bottom-left",
          autoClose: 4000,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        },
      );
    }
  };
  const recordMoonshotEvent = (type, details = {}) => {
    if (!eventLogsRef.current) return;

    const eventData = {
      timestamp: Date.now(),
      eventType: type,
      detail: details,
    };

    eventLogsRef.current.push(eventData);

    sessionStorage.setItem(
      "userInteractionLogs",
      JSON.stringify(eventLogsRef.current),
    );
  };

  const [showMonitorWarning, setShowMonitorWarning] = useState(false);
  const hasLoggedMonitorRef = useRef(false);
  useMultiMonitorDetection({
    pollingInterval: 3000,
    enableLogging: true,

    onMonitorChange: ({ method, count, connected }) => {
      if (connected) {
        setShowMonitorWarning(true);

        // 🔒 log ONLY once per detection session
        if (!hasLoggedMonitorRef.current) {
          recordEventRef.current?.("multi-monitor-detected", {
            monitorCount: count,
            detectionMethod: method,
          });

          hasLoggedMonitorRef.current = true;
        }

        // Optional: freeze interview if required
        // window.__interviewFrozen = true;
      } else {
        setShowMonitorWarning(false);

        // Reset so future reconnects can be logged again
        hasLoggedMonitorRef.current = false;

        recordEventRef.current?.("multi-monitor-disconnected", {
          detectionMethod: method,
        });

        // window.__interviewFrozen = false;
      }
    },
  });

  function onParticipantJoined(participant) {
    participant && participant.setQuality("high");
  }
  const triggerDesktopWarning = () => {
    // Show modal
    setShowDesktopWarning(true);

    // Clear previous timer (important)
    if (desktopWarningTimerRef.current) {
      clearTimeout(desktopWarningTimerRef.current);
    }

    // Auto-close after 15 sec
    desktopWarningTimerRef.current = setTimeout(() => {
      setShowDesktopWarning(false);
    }, 10000);
  };

  function onEntryResponded(participantId, name) {
    if (mMeetingRef.current?.localParticipant?.id === participantId) {
      if (name === "allowed") {
        setLocalParticipantAllowedJoin(true);
      } else {
        setLocalParticipantAllowedJoin(false);
        setTimeout(() => {
          _handleMeetingLeft();
        }, 3000);
      }
    }
  }

  function onMeetingJoined() {
    const companyId = sessionStorage.getItem("companyId");
    setTimeout(() => {
      if (!interviewId) {
        console.error("Interview ID is not available");
        return;
      }
      sessionStorage.setItem("meetingId", meetingId);

      if (
        whiteboardIsReconnect ||
        codingIsReconnect ||
        showWhiteboardRef.current ||
        showCodingRef.current
      ) {
        return;
      }

      try {
        connectInterviewSocket({
          interview_id: interviewId,
          company_id: companyId,
          meeting_room_id: meetingId,
        });

        setSocketConnected(true);
      } catch (error) {
        console.error("Failed to connect to socket:", error);
      }
    }, 1000);
  }

  const [lastKnownSpeaker, setLastKnownSpeaker] = useState(null);
  // inside your component scope
  useEffect(() => {
    const clearTimerRef = { current: null };

    const handlePanelSpeaking = (event) => {
      const { speakerId } = event.detail;
      const panelDetails = JSON.parse(
        sessionStorage.getItem("panelDetails") || "{}",
      );
      const key = speakerId?.toLowerCase();
      const speakerName = panelDetails[key] || speakerId || "Speaker";

      // Save last known speaker
      setLastKnownSpeaker(speakerName);
      setActivePanelSpeaker(speakerName);

      // Reset clear timer
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };

    const handlePanelSpeakingEnded = () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => {
        setActivePanelSpeaker(null);
        clearTimerRef.current = null;
      }, 1000); // 1-second grace delay
    };

    window.addEventListener("panel-speaking", handlePanelSpeaking);
    window.addEventListener("panel-speaking-ended", handlePanelSpeakingEnded);

    return () => {
      window.removeEventListener("panel-speaking", handlePanelSpeaking);
      window.removeEventListener(
        "panel-speaking-ended",
        handlePanelSpeakingEnded,
      );
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, []);
  const forceExitToLogin = () => {
    try {
      // Disable all guards
      window.dispatchEvent(new CustomEvent("disable-interview-guard"));

      // Stop meeting safely
      closeAllSockets();
      window.dispatchEvent(new CustomEvent("force-stop-recording"));
      window.dispatchEvent(new CustomEvent("force-stop-all-media"));

      // Clear storage
      sessionStorage.clear();
      localStorage.removeItem("authToken");
      localStorage.removeItem("userSession");
    } catch (e) {
      console.error("Cleanup failed", e);
    }

    // HARD redirect (no back possible)
    window.location.replace("/screenup-login");
  };

  const hasPushedHistoryRef = useRef(false);

  useEffect(() => {
    if (hasPushedHistoryRef.current) return;

    window.history.pushState({ interview: true }, "");
    hasPushedHistoryRef.current = true;

    const handlePopState = (event) => {
      event.preventDefault();
      setShowExitConfirm(true);

      // lock navigation
      window.history.pushState({ interview: true }, "");
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const whiteboardPendingRef = useRef(false);
  const whiteboardIntervalRef = useRef(null);

  const canOpenWhiteboard = () => {
    if (showWhiteboard) return false; // already open
    if (botAudioActiveRef.current) return false; // bot is speaking
    if (window.__userSpeaking || window.__micActive) return false;
    if (window.__questionnaireMode || window.__questionnaireActive)
      return false;
    return true;
  };

  const openWhiteboard = async ({ isReconnect = false } = {}) => {
    // Interview socket is done for good once the whiteboard opens — never
    // let it reconnect again, even after this overlay closes.
    lockInterviewSocket("whiteboard opened");
    window.__WHITEBOARD_ACTIVE__ = true;
    showWhiteboardRef.current = true;
    sessionStorage.setItem("interviewAlreadyCompleted", "true");
    setActiveWhiteboardIsReconnect(isReconnect);

    setSideBarMode("CHAT");

    // WhiteboardView reads whiteboardQuestions[0].title/.question
    // unconditionally, so the fallback must still be a well-formed entry.
    let questions = [
      {
        title: "Whiteboard",
        question:
          "We couldn't load the question right now — go ahead and use the board, then Submit when you're done.",
      },
    ];
    try {
      const data = await getWhiteboardCodingQuestions({
        whiteboardingEnv: true,
        codingEnv: false,
      });
      if (data?.questions?.questions?.length) {
        questions = data.questions.questions;
      }
    } catch (err) {
      // Don't leave the interview frozen with LST already holding
      // everything and no whiteboard on screen to close it. Fall back to
      // the placeholder above rather than never opening.
      console.error(
        "[Whiteboard] Failed to fetch whiteboard questions — opening anyway:",
        err,
      );
    }

    setWhiteboardQuestion(questions);

    let startIndex = 0;
    if (whiteboardReconnectQuestionId) {
      const matchedIndex = questions.findIndex(
        (q) => q?.question_id === whiteboardReconnectQuestionId,
      );
      if (matchedIndex >= 0) {
        startIndex = matchedIndex; // ← removed the erroneous "- 1"
      } else {
        console.warn(
          "[Whiteboard] Provided question_id not found in fetched " +
            "questions — falling back to question 1:",
          whiteboardReconnectQuestionId,
        );
      }
    }
    setWhiteboardQuestionIndex(startIndex);

    const resolvedQuestionId =
      questions[startIndex]?.question_id ||
      whiteboardReconnectQuestionId ||
      null;
    const newSessionId = resolvedQuestionId
      ? `wb-${interviewId}-${resolvedQuestionId}`
      : `wb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    setWhiteboardSessionId(newSessionId);
    setShowWhiteboard(true);
    window.__WHITEBOARD_SUBMITTED__ = false;
  };
  const openCodingOverlay = async ({ isReconnect = false } = {}) => {
    // Interview socket is done for good once coding opens — never let it
    // reconnect again, even after this overlay closes.
    lockInterviewSocket("coding overlay opened");
    sessionStorage.setItem("interviewAlreadyCompleted", "true");
    setSideBarMode("CHAT");

    let questions = [
      {
        title: "Coding",
        question:
          "We couldn't load the question right now — go ahead and use the board, then Submit when you're done.",
      },
    ];
    try {
      const data = await getCodingQuestions({
        whiteboardingEnv: false,
        codingEnv: true,
      });
      if (data?.questions?.questions.length) {
        questions = data.questions.questions;
      }
    } catch (err) {
      console.error(
        "[Coding] Failed to fetch coding questions — opening anyway:",
        err,
      );
    }
    setCodingQuestion(questions);

    let startIndex = 0;
    if (codingReconnectQuestionId) {
      const matchedIndex = questions.findIndex(
        (q) => q?.question_id === codingReconnectQuestionId,
      );
      if (matchedIndex >= 0) {
        startIndex = matchedIndex;
      } else {
        console.warn(
          "[Coding] Provided question_id not found in fetched " +
            "questions — falling back to question 1:",
          codingReconnectQuestionId,
        );
      }
    }
    setCodingQuestionIndex(startIndex);

    const resolvedQuestionId =
      questions[startIndex]?.question_id || codingReconnectQuestionId || null;
    const newSessionId = resolvedQuestionId
      ? `coding-${interviewId}-${resolvedQuestionId}`
      : `coding-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    window.__CODING_ACTIVE__ = true;
    showCodingRef.current = true;
    setActiveCodingIsReconnect(isReconnect);
    setCodingSessionId(newSessionId);
    setShowCoding(true);
    window.__CODING_SUBMITTED__ = false;
  };

  const advanceToNextWhiteboardQuestion = () => {
    const nextIndex = whiteboardQuestionIndex + 1;

    const nextQuestionId = whiteboardQuestion?.[nextIndex]?.question_id;
    const nextSessionId = nextQuestionId
      ? `wb-${interviewId}-${nextQuestionId}`
      : `wb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setWhiteboardQuestionIndex(nextIndex);
    setWhiteboardSessionId(nextSessionId);

    setActiveWhiteboardIsReconnect(false);
    window.__WHITEBOARD_SUBMITTED__ = false;
  };
  const advanceToNextCodingQuestion = () => {
    const nextIndex = codingQuestionIndex + 1;

    const nextQuestionId = codingQuestion?.[nextIndex]?.question_id;
    const nextSessionId = nextQuestionId
      ? `coding-${interviewId}-${nextQuestionId}`
      : `coding-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setCodingQuestionIndex(nextIndex);
    setCodingSessionId(nextSessionId);

    setActiveCodingIsReconnect(false);
    window.__CODING_SUBMITTED__ = false;
  };

  function onMeetingLeft() {
    // Only fire drop-off when the meeting was left via the confirmed
    // "Discontinue Assessment" flow. Every other mMeeting.leave() call
    // (whiteboard/coding submit, normal end-of-interview redirect, etc.)
    // is a completed session, not a drop-off.
    const wasIntentionalLeave =
      sessionStorage.getItem("intentionalLeave") === "true";

    if (wasIntentionalLeave && interviewId) {
      apiService.setInterviewDropOff(interviewId).then((result) => {
        if (result) {
        } else {
          console.warn(
            "Interview drop-off tracking failed or returned no result",
          );
        }
      });
      sessionStorage.removeItem("intentionalLeave");
    } else if (wasIntentionalLeave) {
      console.warn("No interview ID available for drop-off tracking");
    }
    const screenshotFlag =
      sessionStorage.getItem("mobileProctoringScreenshot") === "true";
    const videoFlag =
      sessionStorage.getItem("mobileProctoringVideo") === "true";
    if (screenshotFlag || videoFlag) {
      (async () => {
        try {
          const companyId = sessionStorage.getItem("companyId");
          const interviewId = sessionStorage.getItem("interviewId");
          const documentId = `${companyId}_${interviewId}`;

          await updateSession(documentId, { status: 999 });
          console.log("Status updated to 999 (EOI)");
        } catch (error) {
          console.error("Error updating session status:", error);
        }
      })();
    }

    // Force stop any ongoing recordings or media
    window.dispatchEvent(new CustomEvent("disable-interview-guard"));
    window.dispatchEvent(new CustomEvent("force-stop-recording"));
    window.dispatchEvent(new CustomEvent("force-stop-all-media"));

    // Stop VideoSDK recording if active
    if (mMeeting?.stopRecording) {
      try {
        mMeeting.stopRecording();
      } catch (error) {
        console.error("Error stopping VideoSDK recording:", error);
      }
    }

    // Stop all media streams
    if (mMeeting?.localParticipant) {
      const streams = [
        mMeeting.localParticipant.micStream,
        mMeeting.localParticipant.webcamStream,
      ];
      streams.forEach((stream) => {
        if (stream?.track) {
          stream.track.stop();
        }
      });
    }

    // Reset media device selections
    setSelectedMic({ id: null, label: null });
    setSelectedWebcam({ id: null, label: null });
    setSelectedSpeaker({ id: null, label: null });

    // Close WebSocket connections
    closeAllSockets();
    setSocketConnected(false);

    // Continue with the existing meeting leave process
    onMeetingLeave();
  }
  const triggerMoonshotCheck = async () => {
    try {
      const documentId = `${companyId}_${interviewId}`;

      // 1️ Show overlay
      window.__interviewFrozen = true;
      openProctoringOverlay();
      overlayStartedRef.current = true;

      // 2️ Update status = 3
      await updateSession(documentId, { status: 3 });

      let completed = false;

      // 3️ Listen for status = 4
      unsubscribeRef.current = listenSession(documentId, (data) => {
        if (!data) return;

        console.log("Firestore update:", data);

        if (data.status === 4) {
          completed = true;

          cleanupListener();

          window.__interviewFrozen = false;
          closeProctoringOverlay();

          recordMoonshotEvent("moonshot_snapshot_completed");
        }
      });

      // 4️ Timeout → 15 sec
      timeoutRef.current = setTimeout(async () => {
        if (!completed && overlayStartedRef.current) {
          cleanupListener();

          window.__interviewFrozen = false;
          closeProctoringOverlay();

          setShowMoonshotWarning(true);
          recordMoonshotEvent("moonshot_snapshot_not_completed");

          //  Timeout status
          await updateSession(documentId, { status: 444 });
        }
      }, 30000);
    } catch (e) {
      closeProctoringOverlay();
    }
  };
  const cleanupListener = () => {
    overlayStartedRef.current = false;
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };
  const canOpenMoonshotSnapshot = () => {
    if (!isMobileProctoringEnabled) return false;
    if (fbkReceivedRef.current) return false;
    // CRITICAL: Check if bot audio is actually playing
    if (botAudioActiveRef.current) return false;

    if (window.__userSpeaking || window.__micActive) return false;
    if (window.__questionnaireMode || window.__questionnaireActive)
      return false;
    if (window.__WHITEBOARD_ACTIVE__) return false;
    if (window.__CODING_ACTIVE__) return false;

    return true;
  };

  const _handleOnError = (data) => {
    const { code, message } = data;

    const joiningErrCodes = [
      4001, 4002, 4003, 4004, 4005, 4006, 4007, 4008, 4009, 4010,
    ];

    const isJoiningError = joiningErrCodes.findIndex((c) => c === code) !== -1;
    const isCriticalError = `${code}`.startsWith("500");

    new Audio(
      isCriticalError
        ? `https://static.videosdk.live/prebuilt/notification_critical_err.mp3`
        : `https://static.videosdk.live/prebuilt/notification_err.mp3`,
    ).play();

    setMeetingErrorVisible(true);
    setMeetingError({
      code,
      message: isJoiningError ? "Unable to join meeting!" : message,
    });
  };

  // MAIN useMeeting hook
  const mMeeting = useMeeting({
    onParticipantJoined,
    onEntryResponded,
    onMeetingJoined,
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
  });

  const {
    isRecording,
    recordingStatus,
    uploadProgress,
    getStatusText,
    pendingUploads,
    stopQARecording, // NEW: Add this
  } = useAutoChunkedRecording(interviewId, mMeeting.isMeetingJoined, mMeeting);

  const { join: meetingJoin, leave: meetingLeave } = mMeeting;
  const { publish: publishChat } = usePubSub("CHAT");
  useEffect(() => {
    const handleBotStartedSpeaking = () => {
      if (pendingBotMessages.length > 0) {
        // Just process the message for local display
        // Don't publish to chat - it was already published by botPublisher
        const processedMessage = processPendingBotMessage();

        if (processedMessage) {
          // processedMessage.message should be a string (text). Use publishChat to actually send it to chat.
        }
      }
    };

    window.addEventListener("bot-started-speaking", handleBotStartedSpeaking);

    return () => {
      window.removeEventListener(
        "bot-started-speaking",
        handleBotStartedSpeaking,
      );
    };
  }, [pendingBotMessages, processPendingBotMessage, publishChat]);

  // for click monitoring start
  useEffect(() => {
    const handleRequestExit = async () => {
      try {
        const documentId = `${companyId}_${interviewId}`;

        //  Update Firestore → user attempting exit
        // await updateSession(documentId, { status: 998 });

        console.log("Status updated to 998 (exit triggered)");
      } catch (err) {
        console.error("Failed to update exit status", err);
      }

      setShowExitConfirm(true); // show custom ConfirmBox
    };

    window.addEventListener("request-exit-confirmation", handleRequestExit);

    return () => {
      window.removeEventListener(
        "request-exit-confirmation",
        handleRequestExit,
      );
    };
  }, []);
  const eventLogsRef = useRef(
    JSON.parse(sessionStorage.getItem("userInteractionLogs")) || [],
  );
  useEffect(() => {
    const recordEvent = (type, detail) => {
      if (window.__questionnaireMode || window.__questionnaireActive) return;
      if (window.__WHITEBOARD_ACTIVE__) return;

      const eventData = {
        timestamp: Date.now(),
        eventType: type,
        detail: detail || {},
      };

      //  push into persistent ref
      eventLogsRef.current.push(eventData);

      //  store merged data
      sessionStorage.setItem(
        "userInteractionLogs",
        JSON.stringify(eventLogsRef.current),
      );
    };
    recordEventRef.current = recordEvent;
    const handleMouse = (e) => {
      recordEvent("mouse", {
        button: e.button,
        x: e.clientX,
        y: e.clientY,
        event: e.type,
      });
    };

    const handleKey = (e) => {
      recordEvent("key", {
        key: e.key,
        code: e.code,
        event: e.type,
      });
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        recordEvent("tab-switch", {
          message: "User switched tab or minimized window",
        });
        setShowTabWarning(true);
      }
    };

    // Track when user is interacting with in-page elements that legitimately steal focus
    // (camera permission, file dialogs, etc. are triggered by user clicking inside the page)
    let lastInPageInteractionTime = 0;
    const handleInPageInteraction = () => {
      lastInPageInteractionTime = Date.now();
    };

    let blurTimeout = null;
    let windowFocused = true;

    const handleWindowBlur = () => {
      if (blurTimeout) clearTimeout(blurTimeout);

      blurTimeout = setTimeout(() => {
        // Skip if: tab is hidden (visibilitychange handles it), or focus returned
        if (document.hidden || document.hasFocus()) return;

        // Skip if a known in-page interaction happened in the last 3 seconds
        // (covers camera permission prompts, file pickers, etc.)
        const timeSinceInteraction = Date.now() - lastInPageInteractionTime;
        if (timeSinceInteraction < 3000) return;

        if (windowFocused) {
          windowFocused = false;
          recordEvent("tab-switch", {
            message: "User switched to a different window",
          });
          setShowTabWarning(true);
        }
      }, 1500);
    };

    const handleWindowFocus = () => {
      if (blurTimeout) clearTimeout(blurTimeout);
      windowFocused = true;
    };

    //  Global listeners (fire anywhere in app)
    window.addEventListener("mousedown", handleMouse);
    window.addEventListener("mouseup", handleMouse);
    window.addEventListener("click", handleMouse);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKey);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);
    // Track clicks and pointer events as "in-page interactions"
    document.addEventListener("mousedown", handleInPageInteraction);
    document.addEventListener("pointerdown", handleInPageInteraction);

    //  When questionnaire closes, you can inspect or send logs
    const handleQuestionnaireClosed = () => {
      // Example: apiService.saveInteractionLogs(interviewId, eventLogs);
    };

    const logQuestionnaireEvent = (type, detail) => {
      const eventData = {
        timestamp: Date.now(),
        eventType: type,
        detail: detail || {},
      };
      eventLogsRef.current.push(eventData);
      sessionStorage.setItem(
        "userInteractionLogs",
        JSON.stringify(eventLogsRef.current),
      );
    };

    //  Questionnaire timed out without the candidate submitting an answer
    const handleNoAnswerQuestionnaire = (e) => {
      logQuestionnaireEvent("Missed-MCQ", {
        questionId: e?.detail?.questionId,
        questionType: e?.detail?.type,
        message: "Questionnaire closed without submitting an answer",
      });
    };

    //  Candidate explicitly clicked "Pass" and the interview had already moved on
    const handleMcqPassed = (e) => {
      logQuestionnaireEvent("MCQ-Passed", {
        questionId: e?.detail?.questionId,
        questionType: e?.detail?.type,
        message: "Candidate passed on the question",
      });
    };

    window.addEventListener("questionnaire-closed", handleQuestionnaireClosed);
    window.addEventListener(
      "no-answer-questionnaire",
      handleNoAnswerQuestionnaire,
    );
    window.addEventListener("mcq-passed", handleMcqPassed);

    return () => {
      window.removeEventListener("mousedown", handleMouse);
      window.removeEventListener("mouseup", handleMouse);
      window.removeEventListener("click", handleMouse);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keyup", handleKey);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("mousedown", handleInPageInteraction);
      document.removeEventListener("pointerdown", handleInPageInteraction);
      if (blurTimeout) clearTimeout(blurTimeout);

      window.removeEventListener(
        "questionnaire-closed",
        handleQuestionnaireClosed,
      );
      window.removeEventListener(
        "no-answer-questionnaire",
        handleNoAnswerQuestionnaire,
      );
      window.removeEventListener("mcq-passed", handleMcqPassed);
    };
  }, []);
  useEffect(() => {
    const handleQuestionnaireModeChanged = (e) => {
      setIsQuestionnaireActive(!!e?.detail?.active);
    };
    window.addEventListener(
      "questionnaire-mode-changed",
      handleQuestionnaireModeChanged,
    );
    return () =>
      window.removeEventListener(
        "questionnaire-mode-changed",
        handleQuestionnaireModeChanged,
      );
  }, []);
  useEffect(() => {
    const handleEndInterviewTriggered = async () => {
      setEndInterviewTriggered(true);

      // Stop all interactions immediately
      window.dispatchEvent(new CustomEvent("force-stop-recording"));
      window.dispatchEvent(new CustomEvent("disable-user-interactions"));
    };

    window.addEventListener(
      "end-interview-triggered",
      handleEndInterviewTriggered,
    );

    return () => {
      window.removeEventListener(
        "end-interview-triggered",
        handleEndInterviewTriggered,
      );
    };
  }, []);

  const handleEndInterviewComplete = () => {
    setTimeout(() => {
      if (mMeeting?.leave) {
        mMeeting.leave();
      } else {
        _handleMeetingLeft();
      }
    }, 1000);
  };

  // ── Whiteboard-reconnect submit: thank-you audio → success redirect ──────
  // In the reconnect flow the whiteboard IS the rest of the session — there's
  // no interview socket holding LST/binary to release, so instead of the
  // normal whiteboard-submitted handoff we play a short thank-you clip and
  // then hard-redirect, the same way the other terminal flows in this file
  // do (see forceExitToLogin / EndInterviewOverlay's onComplete above).
  //
  // `onBeforeRedirect` (recording/media cleanup + mMeeting.leave()) is
  // deliberately run right before the redirect, not before/while the audio
  // plays — force-stop-all-media stops every media element on the page, so
  // firing it earlier was killing the thank-you clip mid-playback and
  // causing the premature redirect.
  const playThankYouAudioAndRedirectToSuccess = ({ onBeforeRedirect } = {}) => {
    let redirected = false;
    const redirectToSuccess = () => {
      if (redirected) return; // "ended" and the fallback timer can race
      redirected = true;
      onBeforeRedirect?.();
      window.__ALLOW_SUCCESS_REDIRECT__ = true;
      window.location.replace("/assessment-success");
    };

    // Safety net: if "ended" never fires for some reason (autoplay block,
    // missing file, decode error, browser quirk), don't leave the
    // candidate stuck on a blank screen forever — redirect once the clip
    // should reasonably be done playing.
    const FALLBACK_MS = 12000;
    const fallbackTimer = setTimeout(redirectToSuccess, FALLBACK_MS);

    try {
      const audio = new Audio(
        `${process.env.PUBLIC_URL || ""}/sounds/thankyou-audio.mp3`,
      );
      audio.addEventListener("ended", () => {
        clearTimeout(fallbackTimer);
        redirectToSuccess();
      });
      // Don't redirect immediately on error/rejected play() — just log it
      // and let the fallback timer above handle the redirect, so a
      // transient decode hiccup doesn't cut the whole thing short.
      audio.addEventListener("error", (e) => {
        console.error("[Whiteboard] Thank-you audio failed to load:", e);
      });
      audio.play().catch((err) => {
        console.error("[Whiteboard] Thank-you audio play() rejected:", err);
      });
    } catch (err) {
      console.error("[Whiteboard] Failed to play thank-you audio:", err);
      // fallback timer still fires
    }
  };

  const ParticipantMicStream = memo(
    ({ participantId }) => {
      const { micStream } = useParticipant(participantId);

      useEffect(() => {
        if (micStream) {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);

          const audioElement = new Audio();
          audioElement.srcObject = mediaStream;
          audioElement.play();
        }
      }, [micStream, participantId]);

      return null;
    },
    [participantsData],
  );

  const { useRaisedHandParticipants } = useMeetingAppContext();
  const bottomBarHeight = 60;

  useEffect(() => {
    containerHeightRef.current = containerHeight;
    containerWidthRef.current = containerWidth;
  }, [containerHeight, containerWidth]);

  // Single source of truth for "is there still bot audio to play/finish".
  // Used both to gate showing the end-interview screen, and passed down to
  // EndInterviewOverlay so IT can also refuse to redirect early (e.g. when
  // the user hits "Discontinue Assessment" mid-question).
  const isBotAudioPending =
    isBotSpeaking ||
    isBotBusy ||
    queuedBotMessages.length > 0 ||
    isQuestionnaireActive ||
    showWhiteboard ||
    showCoding; // holds LST/audio + assessment-success redirect while whiteboard is open

  useEffect(() => {
    if (!endInterviewTriggered) return;

    // Wait until the bot has fully finished: not currently speaking, not busy
    // processing/playing queued audio, and no bot messages still queued up.
    if (isBotAudioPending) return;

    const timer = setTimeout(() => {
      setShowEndInterview(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [endInterviewTriggered, isBotAudioPending]);
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const sideBarContainerWidth = isXLDesktop
    ? 400
    : isLGDesktop
      ? 360
      : isTab
        ? 320
        : isMobile
          ? 280
          : 240;

  useEffect(() => {
    containerRef.current?.offsetHeight &&
      setContainerHeight(containerRef.current.offsetHeight);
    containerRef.current?.offsetWidth &&
      setContainerWidth(containerRef.current.offsetWidth);

    window.addEventListener("resize", ({ target }) => {
      containerRef.current?.offsetHeight &&
        setContainerHeight(containerRef.current.offsetHeight);
      containerRef.current?.offsetWidth &&
        setContainerWidth(containerRef.current.offsetWidth);
    });
  }, [containerRef]);

  const { participantRaisedHand } = useRaisedHandParticipants();

  useEffect(() => {
    if (!publishChat) return;

    // Set chat publisher for other uses
    setChatMessagePublisher(publishChat);

    // Create a bot publisher that socketManager will call
    const botPublisher = (payload) => {
      try {
        // Parse the incoming payload
        let parsed = null;

        if (payload && typeof payload.message === "string") {
          try {
            parsed = JSON.parse(payload.message);
          } catch (e) {
            // Not JSON — create structure
            parsed = {
              senderId: payload.senderId || "anushka-bot",
              senderName: payload.senderName || "Anushka",
              message: payload.message,
              timestamp: payload.timestamp || new Date().toISOString(),
              isBot: true,
            };
          }
        } else {
          parsed = {
            senderId: payload?.senderId || "anushka-bot",
            senderName: payload?.senderName || "Anushka",
            message:
              payload?.message && typeof payload.message === "string"
                ? payload.message
                : JSON.stringify(payload),
            timestamp: payload?.timestamp || new Date().toISOString(),
            isBot: true,
          };
        }

        // Create the chat message payload
        const chatMessage = JSON.stringify({
          senderId: parsed.senderId,
          senderName: parsed.senderName,
          message: parsed.message,
          timestamp: parsed.timestamp,
          isBot: true,
        });

        // Publish to chat so all participants see it
        publishChat(chatMessage, { persist: true });

        // Queue for local display timing (will show when bot starts speaking)
        queueBotMessage({
          senderId: parsed.senderId,
          senderName: parsed.senderName,
          message: parsed.message,
          timestamp: parsed.timestamp,
          isBot: true,
        });
      } catch (err) {
        console.error("botPublisher error:", err, payload);
      }
    };

    // Register publisher in socketManager
    setBotMessagePublisher(botPublisher);

    return () => {
      // Cleanup on unmount
    };
  }, [publishChat, queueBotMessage]);
  useEffect(() => {
    window.mMeeting = mMeeting; // 👈 add this
  }, [mMeeting]);

  useEffect(() => {
    if (readyToJoin && !showCountdown) {
      if (!mMeeting?.isMeetingJoined) {
        meetingJoin();
      }
    }
  }, [readyToJoin, showCountdown, meetingJoin, mMeeting?.isMeetingJoined]);

  // ── Whiteboard reconnect: resume an in-progress whiteboard ────────────────
  // /api/pre-reconnection-check told MeetingDetailsScreen the candidate left
  // mid-whiteboard (is_whiteboarding_completed: false). Once the counter
  // overlay finishes, go straight to the whiteboard — no interview socket
  // connection needed (see the guard in onMeetingJoined below).
  useEffect(() => {
    if (
      whiteboardIsReconnect &&
      !codingIsReconnect &&
      !showCountdown &&
      !whiteboardReconnectHandledRef.current &&
      !showWhiteboardRef.current
    ) {
      whiteboardReconnectHandledRef.current = true;
      sessionStorage.setItem("whiteboarding_env", "true");
      const hasQid = !!readPreReconnectionCheck()?.whiteboard_question_id;
      sessionStorage.removeItem("preReconnectionCheck");

      setPendingRedirectMessage(
        "You have completed the main interview. The Whiteboard session is still pending. Please complete them to finish your interview. Redirecting you now…",
      );
      setShowPendingRedirectOverlay(true);
      setTimeout(() => {
        setShowPendingRedirectOverlay(false);
        openWhiteboard({ isReconnect: hasQid });
      }, 5000);
    }
  }, [whiteboardIsReconnect, codingIsReconnect, showCountdown]);
  useEffect(() => {
    if (
      codingIsReconnect &&
      !showCountdown &&
      !codingReconnectHandledRef.current &&
      !showCodingRef.current
    ) {
      codingReconnectHandledRef.current = true;
      sessionStorage.setItem("coding_env", "true");
      if (whiteboardIsReconnect) {
        sessionStorage.setItem("whiteboarding_env", "true");
      }
      // Clear the blob now that we've acted on it, so a later reload in
      // this same tab (e.g. after the resumed whiteboard is submitted)
      // doesn't re-read this stale reconnect data and think it needs to
      // resume all over again.
      const hasQid = !!readPreReconnectionCheck()?.coding_question_id;
      sessionStorage.removeItem("preReconnectionCheck");
      if (whiteboardIsReconnect)
        setPendingRedirectMessage(
          "You have completed the main interview. The Coding and Whiteboard sessions are still pending. Please complete them to finish your interview. Redirecting you now…",
        );
      else
        setPendingRedirectMessage(
          "You have completed the main interview. The Coding session is still pending. Please complete them to finish your interview. Redirecting you now…",
        );
      setShowPendingRedirectOverlay(true);
      setTimeout(() => {
        setShowPendingRedirectOverlay(false);
        openCodingOverlay({ isReconnect: hasQid });
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codingIsReconnect, whiteboardIsReconnect, showCountdown]);
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (sessionStorage.getItem("intentionalLeave") === "true") return;

      // ADD THIS
      sessionStorage.setItem("pageRefreshed", "true");
      if (window.__ALLOW_SUCCESS_REDIRECT__) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      closeAllSockets();

      if (overlayDelayTimer) {
        clearTimeout(overlayDelayTimer);
      }
      if (overlayDisplayTimer) {
        clearTimeout(overlayDisplayTimer);
      }
    };
  }, [overlayDelayTimer, overlayDisplayTimer]);

  // Socket event listeners
  useEffect(() => {
    const handleSocketConnected = (event) => {
      setSocketConnected(true);
    };

    const handleSocketError = (event) => {
      console.error("Socket error event received:", event.detail);
      setSocketConnected(false);
    };

    const handleSocketClosed = (event) => {
      console.warn(" Socket closed event received:", event.detail);
      setSocketConnected(false);
    };

    window.addEventListener(
      "interview-socket-connected",
      handleSocketConnected,
    );
    window.addEventListener("interview-socket-error", handleSocketError);
    window.addEventListener("interview-socket-closed", handleSocketClosed);

    return () => {
      window.removeEventListener(
        "interview-socket-connected",
        handleSocketConnected,
      );
      window.removeEventListener("interview-socket-error", handleSocketError);
      window.removeEventListener("interview-socket-closed", handleSocketClosed);
    };
  }, []);

  const isPresenting = mMeeting.presenterId ? true : false;
  const userRole =
    mMeeting?.localParticipant?.displayName?.toLowerCase() === "bot"
      ? "bot"
      : "user";

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const participantIds = Array.from(mMeeting.participants.keys());

      setParticipantsData(participantIds);
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [mMeeting.participants]);

  useEffect(() => {
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  // Close the whiteboard automatically if screen sharing/presenting starts
  useEffect(() => {
    if (isPresenting && showWhiteboard) {
      setShowWhiteboard(false);
    }
  }, [isPresenting]);

  const [hasInitializedSidebar, setHasInitializedSidebar] = useState(false);
  useEffect(() => {
    // Set chat panel to open by default when meeting is joined, but only once
    if (mMeeting.isMeetingJoined && !hasInitializedSidebar) {
      setSideBarMode("CHAT");
      setHasInitializedSidebar(true);
    }
  }, [mMeeting.isMeetingJoined, setSideBarMode, hasInitializedSidebar]);
  // To this (add a flag to prevent re-opening after user closes):

  useEffect(() => {
    if (!isBotSpeaking && queuedBotMessages.length > 0) {
      // Optionally clear old queued messages if bot stops speaking
      // This prevents messages from showing up later if there was an error
      const currentTime = Date.now();
      setQueuedBotMessages(
        (prev) => prev.filter((msg) => currentTime - msg.timestamp < 10000), // Keep messages for max 10 seconds
      );
    }
  }, [isBotSpeaking, queuedBotMessages]);

  usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName } = data;

      const isLocal = senderId === localParticipantId;

      new Audio(
        `https://static.videosdk.live/prebuilt/notification.mp3`,
      ).play();

      toast(`${isLocal ? "You" : nameTructed(senderName, 15)} raised hand 🖐🏼`, {
        position: "bottom-left",
        autoClose: 4000,
        hideProgressBar: true,
        closeButton: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });

      participantRaisedHand(senderId);
    },
  });
  useEffect(() => {
    return () => {
      if (desktopWarningTimerRef.current) {
        clearTimeout(desktopWarningTimerRef.current);
      }
      if (headphoneWarningTimerRef.current) {
        clearTimeout(headphoneWarningTimerRef.current);
      }
    };
  }, []);
  useEffect(() => {
    const isDesktopAgentRequired =
      sessionStorage.getItem("desktopAgent") === "true";

    // ❌ If not required → DO NOTHING
    if (!isDesktopAgentRequired) {
      setShowDesktopWarning(false);
      setDesktopAgentRunning(true); // allow flow
      return;
    }

    const companyId = sessionStorage.getItem("companyId");
    const interviewId = sessionStorage.getItem("interviewId");

    if (!companyId || !interviewId) return;

    const documentId = `${companyId}_${interviewId}`;

    let isFirst = true;

    const unsubscribe = listenDesktopAgent(documentId, (data) => {
      console.log("🔥 Firestore Desktop Data:", data);

      const isRunning =
        data?.desktop_app_status === true ||
        data?.desktop_app_status === "true";

      setDesktopAgentRunning(isRunning);

      if (isFirst) {
        desktopPrevStateRef.current = isRunning;
        isFirst = false;

        if (!isRunning && !fbkReceivedRef.current) {
          triggerDesktopWarning();
        }

        return;
      }

      if (desktopPrevStateRef.current !== isRunning) {
        if (!isRunning) {
          // Do not show warning after FBK received
          if (!fbkReceivedRef.current) {
            triggerDesktopWarning();

            recordEventRef.current?.("desktop-agent-stopped", {
              message: "Needle Eye desktop agent not running",
            });
          }
        } else {
          setShowDesktopWarning(false);

          recordEventRef.current?.("desktop-agent-resumed", {
            message: "Needle Eye desktop agent resumed",
          });

          toast.success("Your NeedleEye desktop agent is up and running", {
            style: {
              background: "#16a34a",
              color: "#ffffff",
              fontWeight: "600",
              icon: "",
            },
          });
        }

        desktopPrevStateRef.current = isRunning;
      }
    });

    return () => unsubscribe && unsubscribe();
  }, []);
  useEffect(() => {
    if (!mMeeting?.isMeetingJoined) return;
    if (!isMobileProctoringEnabled) return;
    if (
      fbkReceivedRef.current ||
      sessionStorage.getItem("fbk-received") === "true"
    ) {
      if (moonshotIntervalRef.current) {
        clearInterval(moonshotIntervalRef.current);
        moonshotIntervalRef.current = null;
      }
      sessionStorage.removeItem("moonshotElapsedSeconds");
      return;
    }

    const TARGET_SECONDS = 300; // 5 minutes (5 * 60 seconds)
    const storedSeconds = sessionStorage.getItem("moonshotElapsedSeconds");
    let elapsedSeconds = 0;
    if (storedSeconds) {
      const parsed = parseInt(storedSeconds, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        elapsedSeconds = parsed;
      }
    }

    moonshotIntervalRef.current = setInterval(() => {
      if (!isMobileProctoringEnabled) return;
      if (
        fbkReceivedRef.current ||
        sessionStorage.getItem("fbk-received") === "true"
      ) {
        if (moonshotIntervalRef.current) {
          clearInterval(moonshotIntervalRef.current);
          moonshotIntervalRef.current = null;
        }
        sessionStorage.removeItem("moonshotElapsedSeconds");
        return;
      }

      elapsedSeconds += 1;
      sessionStorage.setItem(
        "moonshotElapsedSeconds",
        elapsedSeconds.toString(),
      );

      if (elapsedSeconds >= TARGET_SECONDS) {
        // Reset counter in sessionStorage and memory
        elapsedSeconds = 0;
        sessionStorage.setItem("moonshotElapsedSeconds", "0");

        if (canOpenMoonshotSnapshot()) {
          // Safe to fire right now
          triggerMoonshotCheck();
        } else {
          // Bot is busy / user is speaking — defer until they finish
          moonshotPendingRef.current = true;
        }
      }
    }, 1000);

    return () => {
      if (moonshotIntervalRef.current) {
        clearInterval(moonshotIntervalRef.current);
        moonshotIntervalRef.current = null;
      }
      closeMoonshotSocket();
      moonshotAbortRef.current?.abort();
    };
  }, [mMeeting?.isMeetingJoined]);

  useEffect(() => {
    const handleBotStartedSpeaking = () => {
      botAudioActiveRef.current = true;
    };

    const checkAndPlayPendingLowBattery = () => {
      botAudioActiveRef.current = false;
      setTimeout(() => {
        if (
          lowBatteryPendingRef.current &&
          !lowBatteryAudioPlayedRef.current &&
          countdownDoneRef.current &&
          !isBotCurrentlySpeaking()
        ) {
          playLowBatteryAudio();
        }
      }, 300);
    };

    const handleBotFinishedSpeaking = () => {
      checkAndPlayPendingLowBattery();

      //  Bot just finished — if a snapshot was deferred, fire it now
      if (moonshotPendingRef.current && canOpenMoonshotSnapshot()) {
        moonshotPendingRef.current = false;
        triggerMoonshotCheck();
      }
    };

    window.addEventListener("bot-started-speaking", handleBotStartedSpeaking);
    window.addEventListener("bot-finished-speaking", handleBotFinishedSpeaking);
    window.addEventListener("bot-queue-empty", checkAndPlayPendingLowBattery);

    return () => {
      window.removeEventListener(
        "bot-started-speaking",
        handleBotStartedSpeaking,
      );
      window.removeEventListener(
        "bot-finished-speaking",
        handleBotFinishedSpeaking,
      );
      window.removeEventListener(
        "bot-queue-empty",
        checkAndPlayPendingLowBattery,
      );
    };
  }, []);

  // Fallback: if bot transitions to free and low battery or snapshot is still pending
  useEffect(() => {
    if (
      lowBatteryPendingRef.current &&
      !lowBatteryAudioPlayedRef.current &&
      countdownDoneRef.current &&
      !isBotBusy &&
      !isBotSpeaking &&
      !isBotCurrentlySpeaking()
    ) {
      playLowBatteryAudio();
    }

    if (!moonshotPendingRef.current) return;
    if (botAudioActiveRef.current) return;
    if (isBotBusy) return;
    if (!canOpenMoonshotSnapshot()) return;
    moonshotPendingRef.current = false;
    triggerMoonshotCheck();
  }, [isBotBusy, showCountdown, activePanelSpeaker, isBotSpeaking]);

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;
      const { senderId, senderName, message } = data;
      const isLocal = senderId === localParticipantId;

      // Check if it's a bot message
      let isBot = false;
      let parsedMessage = null;

      try {
        parsedMessage = JSON.parse(message);
        isBot = parsedMessage.isBot;
      } catch (e) {
        // If not JSON, check if it's an old bot message format
        isBot = message.startsWith("ANUS_BOT: ");
      }

      // Skip all bot messages - they're already handled by botPublisher and queue system
      if (isBot) {
        return;
      }

      // Only process user messages (non-bot, non-local)
      if (!isLocal && !isBot) {
        new Audio(
          `https://static.videosdk.live/prebuilt/notification.mp3`,
        ).play();

        toast(
          `${trimSnackBarText(
            `${nameTructed(senderName, 15)} says: ${message}`,
          )}`,
          {
            position: "bottom-left",
            autoClose: 4000,
            hideProgressBar: true,
            closeButton: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          },
        );
      }
    },
  });
  // Show loading state if interviewId is not available yet
  if (!interviewId) {
    return (
      <div className="fixed inset-0 bg-gray-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading assessment session...</p>
        </div>
      </div>
    );
  }
  if (interviewAlreadyRunning) {
    return (
      <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
        <div className="bg-white p-10 rounded-xl shadow-xl text-center max-w-2xl w-full">
          <h2 className="app-title font-semibold text-red-600 mb-4">
            Interview Already Running
          </h2>
          <p className="app-body mb-6">
            This interview is already active in another tab or browser. Please
            close the other session to continue.
          </p>

          <button
            onClick={() => {
              window.location.replace("/screenup-login");
            }}
            className="px-6 py-2 bg-red-600 text-white btn-size rounded-lg hover:bg-red-700"
          >
            Exit
          </button>
        </div>
      </div>
    );
  }
  if (showLeaveEndOverlay) {
    return (
      <EndInterviewOverlay
        isBotAudioPending={isBotAudioPending}
        onComplete={() => {
          // cleanup + final redirect
          closeAllSockets();
          window.dispatchEvent(new CustomEvent("force-stop-recording"));
          window.dispatchEvent(new CustomEvent("force-stop-all-media"));

          // leave meeting properly
          if (mMeeting?.leave) mMeeting.leave();

          //  redirect to success page
          window.__ALLOW_SUCCESS_REDIRECT__ = true;
          window.location.replace("/assessment-success"); // <-- your success route
        }}
      />
    );
  }

  // CRITICAL: Show end interview screen as separate page when triggered
  if (showEndInterview) {
    return (
      <EndInterviewOverlay
        isBotAudioPending={isBotAudioPending}
        onComplete={handleEndInterviewComplete}
      />
    );
  }

  // Original meeting UI
  return (
    <div className="fixed inset-0">
      {/* Logo and title - hidden when whiteboard is open (WhiteboardView has its own top bar) */}
      <div
        className="absolute top-5 left-4 z-50 mt-[20px] flex items-center space-x-3"
        style={{ display: showWhiteboard ? "none" : "flex" }}
      >
        <img src={whiteLogoImg} alt="logo" />
        {/* <h1 className="text-white text-xl text-heroFontSize font-semibold">
          NeedleInTheHaystack
        </h1> */}
      </div>

      {/* Main meeting container */}
      <div ref={containerRef} className="h-full flex flex-col bg-gray-800">
        <WarningModal
          show={showTabWarning}
          onClose={() => setShowTabWarning(false)}
          message="You switched tabs! Please stay on this tab."
        />
          {" "}
        <WarningModal
          show={showHeadphoneWarning}
          onClose={dismissHeadphoneWarning}
          message="Headphones disconnected. Please reconnect your headphones"
        />
        <WarningModal
          show={isHeadphoneRequired && headphoneNeedsManualConfirmation}
          onClose={confirmHeadphoneManually}
          message="We couldn't automatically confirm your headphones. Click OK to confirm you're wearing headphones."
        />
        {isMobileProctoringEnabled && !interviewAlreadyCompleted && (
          <WarningModal
            show={showMoonshotWarning}
            onClose={() => setShowMoonshotWarning(false)}
            message="You have not completed the Moonshot snapshot. Please complete it immediately."
          />
        )}
        <WarningModal
          show={showMonitorWarning}
          onClose={() => setShowMonitorWarning(false)}
          message="External monitor detected. Please disconnect it immediately. This may impact your overall assessment score."
        />
        {sessionStorage.getItem("desktopAgent") === "true" &&
          !interviewAlreadyCompleted && (
            <WarningModal
              show={showDesktopWarning}
              onClose={() => setShowDesktopWarning(false)}
              message="NeedleEye desktop agent is not running. Please start it. This may impact your overall assessment score."
            />
          )}
        <AudioBot
          isBot={isBot}
          readyToJoin={readyToJoin}
          showCountdown={showCountdown}
          meetingJoined={mMeeting.isMeetingJoined}
        />
        {typeof localParticipantAllowedJoin === "boolean" ? (
          localParticipantAllowedJoin ? (
            <>
              <div className="flex flex-1 flex-row bg-gray-800">
                {showWhiteboard ? (
                  /* ── Whiteboard mode: whiteboard left + participant panel right ── */
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      height: "100%",
                      /* Remove any inherited top margin/padding so whiteboard
                         starts flush at the very top with no black gap */
                      margin: 0,
                      padding: 0,
                      overflow: "hidden",
                    }}
                  >
                    {/* ── Left: Whiteboard fills all remaining width ── */}
                    <div style={{ flex: 1, height: "100%", minWidth: 0 }}>
                      <WhiteboardView
                        key={whiteboardSessionId}
                        sessionId={whiteboardSessionId}
                        meetingId={meetingId}
                        exportRef={whiteboardExportRef}
                        clearRef={whiteboardClearRef}
                        onSubmit={({ isLastQuestion } = {}) => {
                          if (!isLastQuestion) {
                            // Not done yet — move straight into the next
                            // question. Nothing else in the interview flow
                            // (LST, sockets, etc) should react to this;
                            // that all still waits for the LAST question.
                            advanceToNextWhiteboardQuestion();
                            return;
                          }

                          window.__WHITEBOARD_ACTIVE__ = false;
                          window.__WHITEBOARD_SUBMITTED__ = true;
                          if (isWhiteboardReconnect) {
                            setShowWhiteboard(false);
                            playThankYouAudioAndRedirectToSuccess({
                              onBeforeRedirect: () => {
                                window.dispatchEvent(
                                  new CustomEvent("force-stop-recording"),
                                );
                                window.dispatchEvent(
                                  new CustomEvent("force-stop-all-media"),
                                );
                                if (mMeeting?.leave) mMeeting.leave();
                              },
                            });
                            return;
                          }

                          // Flip the flag socketManager checks on LST, then
                          // tell it to release anything it held after LST
                          // (the final bot message + binary) so it can play
                          // now, followed by the normal end-of-interview /
                          // redirect flow.
                          sessionStorage.setItem("whiteboarding_env", "false");
                          window.dispatchEvent(
                            new CustomEvent("whiteboard-submitted"),
                          );
                        }}
                        setShowWhiteboard={setShowWhiteboard}
                        whiteboardQuestions={whiteboardQuestion}
                        questionIndex={whiteboardQuestionIndex}
                        isReconnect={activeWhiteboardIsReconnect}
                        reconnectQuestionId={whiteboardReconnectQuestionId}
                        reconnectElapsedSeconds={whiteboardReconnectDuration}
                        reconnectVideoIndex={whiteboardReconnectVideoIndex}
                      />
                    </div>

                    {/* <WhiteboardPanelSidebar
                      localParticipantId={mMeeting?.localParticipant?.id}
                      activePanelSpeaker={lastKnownSpeaker}
                      isBotSpeaking={isBotSpeaking}
                    /> */}
                  </div>
                ) : showCoding ? (
                  /* ── Coding mode: CodingOverlay left + participant panel right ── */
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      height: "100%",
                      margin: 0,
                      padding: 0,
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ flex: 1, height: "100%", minWidth: 0 }}>
                      <CodingOverlay
                        key={codingSessionId}
                        sessionId={codingSessionId}
                        meetingId={meetingId}
                        codingQuestions={codingQuestion}
                        questionIndex={codingQuestionIndex}
                        isReconnect={activeCodingIsReconnect}
                        reconnectQuestionId={codingReconnectQuestionId}
                        reconnectElapsedSeconds={codingReconnectDuration}
                        resetTimer={resetTimer}
                        onSubmit={({ isLastQuestion } = {}) => {
                          if (!isLastQuestion) {
                            advanceToNextCodingQuestion();
                            setCodingReconnectVideoIndex(0);
                            setResetTimer(true);
                            return;
                          }

                          window.__CODING_ACTIVE__ = false;
                          window.__CODING_SUBMITTED__ = true;

                          if (isCodingReconnect) {
                            setShowCoding(false);
                            const check = readPreReconnectionCheck();
                            const isWhiteboardCompleted =
                              check?.is_whiteboarding_completed === true;
                            const isWhiteboard =
                              sessionStorage.getItem("whiteboarding_env") ===
                                "true" && !isWhiteboardCompleted;
                            if (isWhiteboard && !showWhiteboardRef.current) {
                              if (
                                whiteboardIsReconnect &&
                                !whiteboardReconnectHandledRef.current
                              ) {
                                whiteboardReconnectHandledRef.current = true;
                                openWhiteboard({ isReconnect: true });
                              } else {
                                openWhiteboard();
                              }
                            } else {
                              playThankYouAudioAndRedirectToSuccess({
                                onBeforeRedirect: () => {
                                  window.dispatchEvent(
                                    new CustomEvent("force-stop-recording"),
                                  );
                                  window.dispatchEvent(
                                    new CustomEvent("force-stop-all-media"),
                                  );
                                  if (mMeeting?.leave) mMeeting.leave();
                                },
                              });
                            }
                            return;
                          }

                          sessionStorage.setItem("coding_env", "false");
                          window.dispatchEvent(
                            new CustomEvent("coding-submitted"),
                          );
                          setShowCoding(false);

                          const check = readPreReconnectionCheck();
                          const isWhiteboardCompleted =
                            check?.is_whiteboarding_completed === true;
                          const isWhiteboard =
                            sessionStorage.getItem("whiteboarding_env") ===
                              "true" && !isWhiteboardCompleted;
                          if (isWhiteboard && !showWhiteboardRef.current) {
                            if (
                              whiteboardIsReconnect &&
                              !whiteboardReconnectHandledRef.current
                            ) {
                              whiteboardReconnectHandledRef.current = true;
                              openWhiteboard({ isReconnect: true });
                            } else {
                              openWhiteboard();
                            }
                          }
                        }}
                        setShowCoding={setShowCoding}
                        reconnectVideoIndex={codingReconnectVideoIndex}
                        // onRestart={() => {
                        //   const nextSessionId = `coding-${Date.now()}-${Math.random()
                        //     .toString(36)
                        //     .substring(2, 9)}`;
                        //   setCodingSessionId(nextSessionId);
                        // }}
                      />
                    </div>
                  </div>
                ) : (
                  /* ── Normal mode ─────────────────────────────────────────────── */
                  <>
                    {/* Main content area - takes 75% when sidebar is open, 100% when closed */}
                    <div
                      className="flex bg-gray-800 relative"
                      style={{
                        width:
                          sideBarMode && !isMobile && !isTab ? "75%" : "100%",
                        transition: "width 0.3s ease",
                      }}
                    >
                      {showCountdown && !interviewAlreadyRunning && (
                        <CountdownOverlay
                          onCountdownComplete={() => {
                            setShowCountdown(false);
                            setReadyToJoin(true);
                            countdownDoneRef.current = true;
                            // If low-battery is pending, play it before bot starts
                            if (
                              lowBatteryPendingRef.current &&
                              !lowBatteryAudioPlayedRef.current
                            ) {
                              window.__isLowBatteryAudioPlaying = true;
                              playLowBatteryAudio();
                            }
                            allowBotToStart();
                          }}
                        />
                      )}

                      {isPresenting ? (
                        <PresenterView
                          height={containerHeight - bottomBarHeight}
                          width={
                            sideBarMode && !isMobile && !isTab
                              ? containerWidth * 0.75
                              : containerWidth
                          }
                        />
                      ) : null}
                      {isPresenting && isMobile ? (
                        participantsData.map((participantId) => (
                          <ParticipantMicStream
                            key={participantId}
                            participantId={participantId}
                          />
                        ))
                      ) : (
                        <MemorizedParticipantView
                          isPresenting={isPresenting}
                          isBotSpeaking={isBotSpeaking}
                          activePanelSpeaker={lastKnownSpeaker}
                          sidebarOpen={!!sideBarMode}
                          sidebarWidth={
                            sideBarMode && !isMobile && !isTab
                              ? containerWidth * 0.25
                              : 0
                          }
                        />
                      )}
                    </div>

                    {/* Sidebar - takes 25% when open */}
                    {sideBarMode && (
                      <div
                        className="flex"
                        style={{
                          width: isMobile || isTab ? "100%" : "25%",
                          minWidth: isMobile || isTab ? "auto" : "300px",
                        }}
                      >
                        <SidebarConatiner
                          height={containerHeight}
                          sideBarContainerWidth={
                            isMobile || isTab
                              ? containerWidth
                              : containerWidth * 0.25
                          }
                          activePanelSpeaker={lastKnownSpeaker}
                          showCountdown={showCountdown}
                          isBotSpeaking={isBotSpeaking}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* <BottomBar
                bottomBarHeight={bottomBarHeight}
                setIsMeetingLeft={setIsMeetingLeft}
              /> */}
            </>
          ) : (
            <></>
          )
        ) : (
          !mMeeting.isMeetingJoined && <WaitingToJoinScreen />
        )}
        {userRole === "user" && (
          <>
            <UserVoiceRecorder
              showCountdown={showCountdown}
              useTranscription={useTranscriptionMode}
              contentWidth={
                sideBarMode && !isMobile && !isTab
                  ? containerWidth * 0.75
                  : containerWidth
              }
              sideBarMode={sideBarMode}
              activePanelSpeaker={activePanelSpeaker}
              lastKnownSpeaker={lastKnownSpeaker}
              onActiveSpeakerChange={(name) => setActivePanelSpeaker(name)}
              stopQARecording={stopQARecording} // NEW: Pass the function
            />
          </>
        )}
        {showPendingRedirectOverlay && (
          <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70"
            style={{
              zIndex: 999999,
              paddingRight: sideBarMode && !isMobile && !isTab ? "25%" : "0",
            }}
          >
            <div className="text-white text-2xl font-semibold flex items-center max-w-3xl text-center px-4 leading-relaxed">
              <svg
                className="animate-spin -ml-1 mr-3 h-6 w-6 text-white shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {pendingRedirectMessage}
            </div>
          </div>
        )}
        <ConfirmBox
          open={meetingErrorVisible}
          successText="OKAY"
          onSuccess={() => {
            setMeetingErrorVisible(false);
          }}
          title={`Error Code: ${meetingError.code}`}
          subTitle={meetingError.message}
        />
        <ConfirmBox
          open={showExitConfirm}
          title="Discontinue Assessment?"
          subTitle="Are you sure you want to leave the assessment?"
          successText="Yes"
          rejectText="No"
          onSuccess={async () => {
            setShowExitConfirm(false);
            try {
              const documentId = `${companyId}_${interviewId}`;

              //  Update Firestore → FINAL COMPLETE
              await updateSession(documentId, { status: 999 });

              console.log("Status updated to 999 (EOI)");
            } catch (err) {
              console.error("Failed to update EOI status", err);
            }
            sessionStorage.setItem("intentionalLeave", "true");
            const interviewId = sessionStorage.getItem("interviewId");
            //  CALL PERIPHERAL API FIRST
            try {
              await sendPeripheralRecord();
            } catch (err) {
              console.error("Peripheral API failed", err);
            }
            // Stop media first
            window.dispatchEvent(new CustomEvent("force-stop-recording"));
            window.dispatchEvent(new CustomEvent("force-stop-all-media"));

            // Close interview socket first, THEN call drop-off, THEN close everything else
            closeInterviewSocketWithCallback(async () => {
              if (interviewId) {
                try {
                  await apiService.setInterviewDropOff(interviewId);
                } catch (err) {
                  console.error("Drop-off API failed", err);
                }
              }

              closeAllSockets(); // close transcribe + cleanup queues
              window.dispatchEvent(new CustomEvent("force-stop-recording"));
              window.dispatchEvent(new CustomEvent("force-stop-all-media"));
              setShowLeaveEndOverlay(true);
            });
          }}
          onReject={() => {
            setShowExitConfirm(false);
          }}
        />
      </div>
      {/* {showMonitorWarning && <MonitorDetectionOverlay />} */}
      {isMobileProctoringEnabled && !interviewAlreadyCompleted && (
        <MoonshotProctoringOverlay open={showMoonshotOverlay} />
      )}
    </div>
  );
}

export default MemorizedParticipantView;
